import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'

// Extend dayjs with duration plugin
dayjs.extend(duration)

/**
 * Special timestamp value that represents "never" timeout
 */
export const NEVER_TIMESTAMP = -1

/**
 * Formats timeout duration from milliseconds to human-readable format
 * @param duration - Duration in milliseconds, Date, or null/undefined
 * @returns Formatted duration string
 */
export function formatTimeoutDuration(duration: number | Date | null | undefined): string {
	if (duration === NEVER_TIMESTAMP) {
		return 'Never'
	}

	if (typeof duration === 'number' && duration > 0) {
		const durationObj = dayjs.duration(duration)

		// Calculate total days
		const days = Math.floor(durationObj.asDays())
		const hours = Math.floor(durationObj.asHours() % 24)
		const minutes = Math.floor(durationObj.asMinutes() % 60)
		const seconds = Math.floor(durationObj.asSeconds() % 60)

		const parts: string[] = []
		if (days > 0) {
			parts.push(`${days} day${days > 1 ? 's' : ''}`)
		}
		if (hours > 0) {
			parts.push(`${hours} hr${hours > 1 ? 's' : ''}`)
		}
		if (minutes > 0) {
			parts.push(`${minutes} min${minutes > 1 ? 's' : ''}`)
		}
		if (seconds > 0) {
			parts.push(`${seconds} sec${seconds > 1 ? 's' : ''}`)
		}

		return parts.length > 0 ? parts.join(', ') : '0 seconds'
	}

	return 'No Timeout Set'
}

/**
 * Formats timeout duration from seconds to human-readable format
 * @param timeoutSeconds - Duration in seconds
 * @returns Formatted duration string
 */
export function formatTimeoutDurationFromSeconds(timeoutSeconds: number | null | undefined): string {
	if (timeoutSeconds === null || timeoutSeconds === undefined) {
		return 'Never'
	}

	if (timeoutSeconds === NEVER_TIMESTAMP) {
		return 'Never'
	}

	if (timeoutSeconds <= 0) {
		return 'Never'
	}

	// Convert seconds to milliseconds for dayjs duration
	const durationMs = timeoutSeconds * 1000
	return formatTimeoutDuration(durationMs)
}

/**
 * Gets timeout display value for form inputs
 * @param timeout - Timeout value in seconds
 * @returns Display string for timeout
 */
export function getTimeoutDisplayValue(timeout: number | null | undefined): string {
	if (timeout === null || timeout === undefined) {
		return 'No timeout'
	}

	if (timeout === NEVER_TIMESTAMP) {
		return 'Never'
	}

	return formatTimeoutDurationFromSeconds(timeout)
}
